import {
  WidgetConfig,
  WidgetEvent,
  WidgetEventHandler,
  WidgetEventType,
} from "../../types";
import flatpickr from "flatpickr";
import { APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, initFlatpickrInShadow, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from "../../helpers";

export class TraderverseMarketSectorValuationHeatmap {
  private container: HTMLElement;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private config: WidgetConfig;
  private widgetResizeObserver: ResizeObserver | null = null;
  private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
  private isLoading: boolean = true;

  private currentData: any[] = [];
  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: "closed" });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: "AAPL", value: "AAPL" },
      { label: "MSFT", value: "MSFT" },
      { label: "GOOGL", value: "GOOGL" },
      { label: "AMZN", value: "AMZN" },
      { label: "NVDA", value: "NVDA" },
      { label: "META", value: "META" },
      { label: "TSLA", value: "TSLA" },
      { label: "AVGO", value: "AVGO" },
      { label: "PEP", value: "PEP" },
      { label: "COST", value: "COST" },
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: "default-dark",
      width: "100%",
      height: "400px",
      autosize: true,
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === "string") {
      const el =
        document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.isLoading = true;
    await this.attachStyles();
    this.currentData = [];
    this.updateDisplay();
    this.emit("ready", {});
    this.config.onReady?.();
    this.setupChart();
    cancelSelectEvent(this.shadowRoot);
  }

  private async attachStyles(): Promise<void> {
    try {
      appendFlatpickrBaseStyles(this.shadowRoot);
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error("Failed to load CSS file:", err);
    }
  }

  private async setupChart(): Promise<void> {
    try {
      const cfg = this.config as any;
      const params: Record<string, string> = {};
      params.sectorType = cfg.sectorType || 'All';
      params.start_date = cfg.start_date || '2024-01-01';
      params.end_date = cfg.end_date || '2024-06-30';
      params.min_pe_threshold = typeof cfg.min_pe_threshold !== 'undefined' ? String(cfg.min_pe_threshold) : '10';
      params.max_pe_threshold = typeof cfg.max_pe_threshold !== 'undefined' ? String(cfg.max_pe_threshold) : '30';
      params.color_scale_min = typeof cfg.color_scale_min !== 'undefined' ? String(cfg.color_scale_min) : '0';
      params.color_scale_max = typeof cfg.color_scale_max !== 'undefined' ? String(cfg.color_scale_max) : '50';
      params.sort_by = cfg.sort_by || 'pe_value';
      const qs = Object.keys(params).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`).join('&');
      const url = `${APIURL}/v1/discover/sector-valuation-heatmap?${qs}`;

      const response = await fetch(url, {
        headers: { },
      });

      if (!response.ok) throw new Error("API fetch failed");

      const result = await response.json();
      if (Array.isArray(result.data)) {
        this.currentData = result.data;
      } else if (result.data && Array.isArray(result.data.data)) {
        this.currentData = result.data.data;
      } else {
        this.currentData = [];
      }
      this.isLoading = false;
      this.updateDisplay();
      this.emit("dataUpdate", this.currentData);
    } catch (err) {
      this.currentData = [];
      this.isLoading = false;
      this.updateDisplay();
      console.error("Failed to fetch API data:", err);
    }
  }

  private updateDisplay(): void {
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    if (widget) {
      if (this.isLoading) {
        widget.classList.add("loading");
      } else {
        widget.classList.remove("loading");
      }
    }
    if (!this.currentData) {
      return;
    }
    this.renderHeatmap();
  }

  private renderHeatmap(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
  let itemsHTML = "";
  if (!this.currentData || this.currentData.length === 0) {
    itemsHTML = getNoDataHTML();
  } else {

    itemsHTML = this.currentData
    
      .map(
        (item) => `
        <div class="item ${item.valuation_status?.toLowerCase()}">
          <div class="rowFirst">
            <h3>${item.sector}</h3>
            <h2>${item.pe_difference_percent}% <span>AVG ${item.avg_pe}</span></h2>
          </div>
          <div class="rowSecond">
            <p>${item.pe_difference_percent} <span>${item.avg_pe}</span></p>
            <status>${item.valuation_status}</status>
          </div>
        </div>
      `
      )
      .join("");
  }
  

  const widgetHtml = `
    <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass}${this.isLoading ? ' loading' : ''}" data-theme="${dataTheme}">
      <div class="headTop">
        <h4 class="title market">${this.getTitleIconSvg()} Sector Valuation Heatmap</h4>
        <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
          <div class="item">
            <button class="filters-toggle" type="button" aria-label="Filters" aria-expanded="false"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
          </div>
        </div>
      </div>
      <div class="inBody">
        <div class="filtersBox sideBar hidden">
          <div class="item">
            <label>Sector Type</label>
            <select class="filter-sector-type">
              <option value="All">All</option>
              <option value="Technology">Technology</option>
              <option value="Finance">Finance</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Energy">Energy</option>
              <option value="Consumer">Consumer</option>
              <option value="Utilities">Utilities</option>
              <option value="Industrials">Industrials</option>
              <option value="Materials">Materials</option>
              <option value="RealEstate">Real Estate</option>
              <option value="Communication">Communication</option>
            </select>
          </div>
          <div class="item dateRange">
            <label>Date Range</label>
            <input type="text" class="date-input" readonly />
          </div>
          <div class="item">
            <label>PE Threshold</label>
            <div class="fieldsGroup">
              <input type="number" class="filter-min-pe" min="0" step="1" value="${(this.config as any).min_pe_threshold ?? 10}" aria-label="Minimum PE" />
              <input type="number" class="filter-max-pe" min="0" step="1" value="${(this.config as any).max_pe_threshold ?? 30}" aria-label="Maximum PE" />
            </div>
          </div>
          <div class="item">
            <label>Color Scale</label>
            <div class="fieldsGroup">
              <input type="number" class="filter-color-scale-min" min="0" step="1" value="${(this.config as any).color_scale_min ?? 0}" aria-label="Color scale minimum" />
              <input type="number" class="filter-color-scale-max" min="0" step="1" value="${(this.config as any).color_scale_max ?? 50}" aria-label="Color scale maximum" />
            </div>
          </div>
          <div class="item">
            <label>Sort By</label>
            <select class="filter-sort-by">
              <option value="pe_value">PE Value</option>
              <option value="market_cap">Market Cap</option>
            </select>
          </div>
        </div>
        <div class="chartDv">
          <div class="secValHeatmapMain">
            ${itemsHTML}
          </div>
        </div>
      </div>
    </div>
  `;
  const existingWidgetBox = this.shadowRoot.querySelector('.widgetBox');
  if (existingWidgetBox) {
    existingWidgetBox.outerHTML = widgetHtml.trim();
  } else {
    const temp = document.createElement('div');
    temp.innerHTML = widgetHtml;
    this.shadowRoot.appendChild(temp.firstElementChild!);
  }
  const heatmapEl = this.shadowRoot.querySelector('.secValHeatmapMain') as HTMLElement;

if (this.currentData && this.currentData.length > 0) {
  heatmapEl?.setAttribute('data-testing-id', 'sector-valuation-heatmap');
} else {
  heatmapEl?.removeAttribute('data-testing-id');
}
  // Date picker logic (flatpickr)
  const dateInput = this.shadowRoot.querySelector('.date-input') as HTMLInputElement | null;
  if (dateInput) {
    let defaultDates: string[] | undefined = undefined;
    if (this.config.start_date && this.config.end_date) {
      defaultDates = [this.config.start_date, this.config.end_date];
      dateInput.value = `${this.config.start_date} to ${this.config.end_date}`;
    }
    initFlatpickrInShadow(dateInput, this.shadowRoot, {
      mode: 'range',
      dateFormat: 'Y-m-d',
      defaultDate: defaultDates,
      onClose: (selectedDates: Date[]) => {
        if (selectedDates.length === 2) {
          const start = selectedDates[0].toISOString().slice(0, 10);
          const end = selectedDates[1].toISOString().slice(0, 10);
          this.config.start_date = start;
          this.config.end_date = end;
          dateInput.value = `${start} to ${end}`;
          this.setupChart();
        }
      },
    });
  }

  // Add event listeners for filters and toggling
  if (this.outsideClickHandler) {
    document.removeEventListener('click', this.outsideClickHandler);
    this.outsideClickHandler = null;
  }
  const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
  const filtersBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
  if (filtersBtn && filtersBox) {
    filtersBtn.addEventListener('click', (ev: MouseEvent) => {
      ev.stopPropagation();
      filtersBox.classList.toggle('hidden');
      const isOpen = !filtersBox.classList.contains('hidden');
      filtersBtn.classList.toggle('active', isOpen);
      filtersBtn.setAttribute('aria-expanded', String(isOpen));
    });
    filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
    filtersBox.addEventListener('pointerdown', (ev: PointerEvent) => ev.stopPropagation());
    this.outsideClickHandler = (ev: MouseEvent) => {
      try {
        const path = (ev as any).composedPath ? (ev as any).composedPath() : (ev as any).path || [];
        const clickedInside = Array.isArray(path) && path.some((node: any) => {
          try {
            return node && node.getRootNode && node.getRootNode() === this.shadowRoot;
          } catch (e) {
            return false;
          }
        });
        if (!clickedInside) {
          filtersBox.classList.add('hidden');
          filtersBtn.classList.remove('active');
          filtersBtn.setAttribute('aria-expanded', 'false');
        }
      } catch (e) {
        filtersBox.classList.add('hidden');
        filtersBtn.classList.remove('active');
        filtersBtn.setAttribute('aria-expanded', 'false');
      }
    };
    document.addEventListener('click', this.outsideClickHandler);
  }

  // Filter event listeners
  const sectorTypeEl = this.shadowRoot.querySelector('.filter-sector-type') as HTMLSelectElement | null;
  const startDateEl = this.shadowRoot.querySelector('.filter-start-date') as HTMLInputElement | null;
  const endDateEl = this.shadowRoot.querySelector('.filter-end-date') as HTMLInputElement | null;
  const minPeEl = this.shadowRoot.querySelector('.filter-min-pe') as HTMLInputElement | null;
  const maxPeEl = this.shadowRoot.querySelector('.filter-max-pe') as HTMLInputElement | null;
  const colorScaleMinEl = this.shadowRoot.querySelector('.filter-color-scale-min') as HTMLInputElement | null;
  const colorScaleMaxEl = this.shadowRoot.querySelector('.filter-color-scale-max') as HTMLInputElement | null;
  const sortByEl = this.shadowRoot.querySelector('.filter-sort-by') as HTMLSelectElement | null;
  [
    sectorTypeEl,
    dateInput,
    minPeEl,
    maxPeEl,
    colorScaleMinEl,
    colorScaleMaxEl,
    sortByEl,
  ].forEach((el) =>
    el?.addEventListener('pointerdown', (ev: Event) => {
      if (ev instanceof PointerEvent) ev.stopPropagation();
    })
  );

  if (sectorTypeEl) {
    sectorTypeEl.value = (this.config as any).sectorType || 'All';
    sectorTypeEl.addEventListener('change', () => {
      (this.config as any).sectorType = sectorTypeEl.value;
      this.setupChart();
    });
  }
  if (startDateEl) {
    startDateEl.value = this.config.start_date || '2024-01-01';
    startDateEl.addEventListener('change', () => {
      this.config.start_date = startDateEl.value;
      this.setupChart();
    });
  }
  if (endDateEl) {
    endDateEl.value = this.config.end_date || '2024-06-30';
    endDateEl.addEventListener('change', () => {
      this.config.end_date = endDateEl.value;
      this.setupChart();
    });
  }
  if (minPeEl) {
    minPeEl.value = String((this.config as any).min_pe_threshold ?? 10);
    minPeEl.addEventListener('change', () => {
      (this.config as any).min_pe_threshold = Number(minPeEl.value);
      this.setupChart();
    });
  }
  if (maxPeEl) {
    maxPeEl.value = String((this.config as any).max_pe_threshold ?? 30);
    maxPeEl.addEventListener('change', () => {
      (this.config as any).max_pe_threshold = Number(maxPeEl.value);
      this.setupChart();
    });
  }
  if (colorScaleMinEl) {
    colorScaleMinEl.value = String((this.config as any).color_scale_min ?? 0);
    colorScaleMinEl.addEventListener('change', () => {
      (this.config as any).color_scale_min = Number(colorScaleMinEl.value);
      this.setupChart();
    });
  }
  if (colorScaleMaxEl) {
    colorScaleMaxEl.value = String((this.config as any).color_scale_max ?? 50);
    colorScaleMaxEl.addEventListener('change', () => {
      (this.config as any).color_scale_max = Number(colorScaleMaxEl.value);
      this.setupChart();
    });
  }
  if (sortByEl) {
    sortByEl.value = (this.config as any).sort_by || 'pe_value';
    sortByEl.addEventListener('change', () => {
      (this.config as any).sort_by = sortByEl.value;
      this.setupChart();
    });
  }
}


  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (stockId: any) => this.setupChart(),
      (type: any, data: any) => this.emit(type, data),
    );
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list)
      this.eventHandlers.set(
        type,
        list.filter((h) => h !== handler),
      );
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector(
      ".symbol-dropdown",
    ) as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = "";
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((h) => h(evt));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window {
    TraderverseMarketSectorValuationHeatmap?: typeof TraderverseMarketSectorValuationHeatmap;
  }
}

if (typeof window !== "undefined") {
  window.TraderverseMarketSectorValuationHeatmap =
    TraderverseMarketSectorValuationHeatmap;
}